import {
  IconButton,
  Icons,
  Input,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@commenda/kakashi';
import { useMemo, useState } from 'react';

import { useReport } from '../context/ReportContext';
import {
  BALANCE_SHEET_BUCKETS,
  BUCKET_LABELS,
  BucketHelper,
  PNL_BUCKETS,
} from '../helpers/BucketHelper';
import { ReportFormatHelper } from '../helpers/ReportFormatHelper';
import { ReportingBucket, type ILedgerLine } from '../types/buckets';

interface IBucketMappingSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const BS_OPTIONS = [...BALANCE_SHEET_BUCKETS, ReportingBucket.EXCLUDED];
const PNL_OPTIONS = [...PNL_BUCKETS, ReportingBucket.EXCLUDED];

const isBucket = (value: string): value is ReportingBucket =>
  value in BUCKET_LABELS;

const optionsFor = (line: ILedgerLine) =>
  line.statement === 'BS' ? BS_OPTIONS : PNL_OPTIONS;

/** Ledger line → reporting bucket editor. Changing a mapping rebuilds the fact table immediately. */
export const BucketMappingSheet = ({
  open,
  onOpenChange,
}: IBucketMappingSheetProps) => {
  const { table, currency, bucketOverrides, setBucketOverride } = useReport();
  const [query, setQuery] = useState('');

  const lines = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const lastMonth = table.months[table.months.length - 1];
    const lastFacts = lastMonth ? table.byMonth.get(lastMonth) : undefined;
    return table.lines
      .filter(
        (line) =>
          !needle ||
          line.name.toLowerCase().includes(needle) ||
          line.path.join(' ').toLowerCase().includes(needle)
      )
      .map((line) => ({ line, lastValue: lastFacts?.lines[line.id] ?? 0 }));
  }, [table, query]);

  const handleChange = (line: ILedgerLine, value: string) => {
    if (!isBucket(value)) return;
    const isDefault = value === BucketHelper.defaultBucketFor(line);
    setBucketOverride(line.id, isDefault ? null : value);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
      >
        <SheetHeader className="border-b border-border-secondary">
          <SheetTitle>Ledger to bucket mapping</SheetTitle>
          <SheetDescription>
            Components read buckets, never raw ledgers. Changing a mapping
            recomputes every figure immediately.{' '}
            {Object.keys(bucketOverrides).length} override(s) active.
          </SheetDescription>
        </SheetHeader>
        <div className="border-b border-border-secondary p-lg">
          <Input
            placeholder="Filter ledger lines…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <ScrollArea className="min-h-0 flex-1">
          <ul className="divide-y divide-border-secondary">
            {lines.map(({ line, lastValue }) => {
              const current =
                table.bucketMap.get(line.id) ?? ReportingBucket.EXCLUDED;
              const isOverridden = line.id in bucketOverrides;
              return (
                <li
                  key={line.id}
                  className="flex items-center gap-lg px-xl py-md txt-small-regular"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate txt-small-medium text-text-primary">
                      {line.name}
                    </p>
                    <p className="truncate txt-extra-small-regular text-text-tertiary">
                      {line.path.join(' › ')} · last{' '}
                      {ReportFormatHelper.currency(lastValue, currency, {
                        compact: true,
                      })}
                    </p>
                  </div>
                  <Select
                    value={current}
                    onValueChange={(value) => handleChange(line, value)}
                  >
                    <SelectTrigger className="w-[220px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {optionsFor(line).map((bucket) => (
                        <SelectItem key={bucket} value={bucket}>
                          {BUCKET_LABELS[bucket]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <IconButton
                    size="sm"
                    disabled={!isOverridden}
                    aria-label="Reset to default mapping"
                    icon={<Icons.RefreshCcw01 className="size-4" />}
                    onClick={() => setBucketOverride(line.id, null)}
                  />
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
